import { supabase } from '@/lib/supabase';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: unknown;
}

interface QueuedLog extends LogEntry {
  repeat: number;
  lastAt: number;
}

const MAX_STORED_LOGS = 200;

/**
 * Journal système du code.
 *
 * • Toujours conservé en session (200 dernières entrées) pour le débogage.
 * • Si `VITE_PUBLIC_ENABLE_SYSTEM_LOGS` vaut « true » dans le fichier .env,
 *   chaque entrée est aussi envoyée par lots dans `platform_system_logs`
 *   afin d'apparaître dans le journal du SuperAdmin (/superadmin/logs).
 *   Les entrées identiques rapprochées sont fusionnées (compteur) pour ne
 *   pas saturer la base.
 */
const REMOTE_ENABLED = String(import.meta.env.VITE_PUBLIC_ENABLE_SYSTEM_LOGS ?? 'true')
  .trim()
  .toLowerCase() !== 'false';

const FLUSH_DELAY_MS = 3000;
const MAX_BATCH = 40;
const DEDUPE_WINDOW_MS = 8000;

class Logger {
  private logs: LogEntry[] = [];
  private level: LogLevel = 'debug';
  private queue: QueuedLog[] = [];
  private flushTimer: number | null = null;
  private flushing = false;

  constructor() {
    // Load persisted logs
    try {
      const stored = sessionStorage.getItem('rdy_app_logs');
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch {
      // ignore
    }

    if (REMOTE_ENABLED && typeof window !== 'undefined') {
      // Dernier flush quand l'onglet passe en arrière-plan / se ferme.
      window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') void this.flush();
      });
      window.addEventListener('pagehide', () => {
        void this.flush();
      });
    }
  }

  private persist(): void {
    try {
      const toStore = this.logs.slice(-MAX_STORED_LOGS);
      sessionStorage.setItem('rdy_app_logs', JSON.stringify(toStore));
    } catch {
      // ignore
    }
  }

  private formatTimestamp(): string {
    const now = new Date();
    return now.toISOString().replace('T', ' ').slice(0, 23);
  }

  private enqueue(entry: LogEntry): void {
    if (!REMOTE_ENABLED || typeof window === 'undefined') return;
    const now = Date.now();
    const existing = this.queue.find(
      (q) =>
        q.level === entry.level &&
        q.category === entry.category &&
        q.message === entry.message &&
        now - q.lastAt < DEDUPE_WINDOW_MS,
    );
    if (existing) {
      existing.repeat += 1;
      existing.lastAt = now;
      existing.data = entry.data;
    } else {
      this.queue.push({ ...entry, repeat: 1, lastAt: now });
      if (this.queue.length > 120) this.queue.splice(0, this.queue.length - 120);
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null || this.flushing) return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, FLUSH_DELAY_MS);
  }

  private async flush(): Promise<void> {
    if (!REMOTE_ENABLED || this.flushing || this.queue.length === 0) return;
    this.flushing = true;
    const batch = this.queue.splice(0, MAX_BATCH);
    try {
      let userId: string | null = null;
      let userLabel: string | null = null;
      try {
        const { data } = await supabase.auth.getSession();
        const authUser = data.session?.user;
        if (authUser) {
          userId = authUser.id;
          userLabel = authUser.email || null;
        }
      } catch {
        /* session indisponible */
      }

      const page = typeof window !== 'undefined' ? window.location.pathname || '/' : '';
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 400) : '';

      const rows = batch.map((entry) => {
        let payload: unknown = entry.data === undefined ? null : entry.data;
        if (entry.repeat > 1) {
          payload = { repeat: entry.repeat, last: entry.data ?? null };
        }
        return {
          level: entry.level,
          category: entry.category,
          message: entry.message.slice(0, 900),
          data: sanitizeData(payload),
          page,
          user_id: userId,
          user_label: userLabel,
          user_agent: userAgent,
          repeat_count: entry.repeat,
          created_at: new Date().toISOString(),
        };
      });

      const { error } = await supabase.from('platform_system_logs').insert(rows);
      if (error) throw error;
    } catch {
      /* le journal distant ne doit jamais casser l'application */
    } finally {
      this.flushing = false;
      if (this.queue.length > 0) this.scheduleFlush();
    }
  }

  log(level: LogLevel, category: string, message: string, data?: unknown): void {
    const entry: LogEntry = {
      timestamp: this.formatTimestamp(),
      level,
      category,
      message,
      data,
    };

    this.logs.push(entry);
    if (this.logs.length > MAX_STORED_LOGS * 2) {
      this.logs = this.logs.slice(-MAX_STORED_LOGS);
    }
    this.persist();
    this.enqueue(entry);

    const prefix = `[${entry.timestamp}] [${level.toUpperCase()}] [${category}]`;
    const dataStr = data !== undefined ? ` ${JSON.stringify(data)}` : '';

    switch (level) {
      case 'debug':
        console.debug(`${prefix} ${message}${dataStr}`);
        break;
      case 'info':
        console.info(`${prefix} ${message}${dataStr}`);
        break;
      case 'warn':
        console.warn(`${prefix} ${message}${dataStr}`);
        break;
      case 'error':
        console.error(`${prefix} ${message}${dataStr}`);
        break;
    }
  }

  debug(category: string, message: string, data?: unknown): void {
    this.log('debug', category, message, data);
  }

  info(category: string, message: string, data?: unknown): void {
    this.log('info', category, message, data);
  }

  warn(category: string, message: string, data?: unknown): void {
    this.log('warn', category, message, data);
  }

  error(category: string, message: string, data?: unknown): void {
    this.log('error', category, message, data);
  }

  getLogs(level?: LogLevel, category?: string): LogEntry[] {
    let filtered = [...this.logs];
    if (level) {
      filtered = filtered.filter((l) => l.level === level);
    }
    if (category) {
      filtered = filtered.filter((l) => l.category === category);
    }
    return filtered.slice(-100);
  }

  getAllLogs(): LogEntry[] {
    return this.logs.slice(-100);
  }

  getRecentErrors(): LogEntry[] {
    return this.logs
      .filter((l) => l.level === 'error')
      .slice(-20);
  }

  clear(): void {
    this.logs = [];
    sessionStorage.removeItem('rdy_app_logs');
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  /** Le journal distant (SuperAdmin) est-il activé par le fichier .env ? */
  get remoteEnabled(): boolean {
    return REMOTE_ENABLED;
  }

  /** Force l'envoi immédiat de la file (utile au chargement du journal). */
  async flushNow(): Promise<void> {
    await this.flush();
  }

  /** Returns a formatted string of recent errors for display */
  getErrorSummary(): string {
    const errors = this.getRecentErrors();
    if (errors.length === 0) return '';
    return errors
      .map((e) => `[${e.timestamp}] ${e.category}: ${e.message}`)
      .join('\n');
  }
}

/** Sérialise les données de log en JSON sûr et borné. */
function sanitizeData(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return null;
  if (depth > 3) return '[…]';
  if (typeof value === 'string') return value.length > 1200 ? `${value.slice(0, 1200)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: (value.stack || '').slice(0, 1200) };
  }
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => sanitizeData(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>)
      .slice(0, 30)
      .forEach(([k, v]) => {
        if (/token|authorization|apikey|api_key|password|secret/i.test(k)) {
          out[k] = '[masqué]';
        } else {
          out[k] = sanitizeData(v, depth + 1);
        }
      });
    return out;
  }
  return String(value);
}

export const logger = new Logger();

// Export for global access in dev tools
if (typeof window !== 'undefined') {
  (window as Record<string, unknown>).__rdyLogger = logger;
}